import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useQuery } from "@msuite/ui/query";
import { Skeleton } from "@msuite/ui/skeleton";
import { responseHandler } from "@msuite/ui/utils";
import type { NotesTable } from "@msuite/utils/types";
import { UserCard } from "@/components/cards";
import { client } from "@/hono";

/** Props Interface */
interface MarkedAsReplyHintProps {
	markedAsReply: string | null;
	resetMarkedAsReply: (_null: string | null) => void;
	notesTable: NotesTable;
	identifier: string;
}

export function MarkedAsReplyHint({
	markedAsReply,
	resetMarkedAsReply,
	notesTable,
	identifier,
}: MarkedAsReplyHintProps) {
	/** Hooks */
	const { data: note, isFetching } = useQuery({
		queryKey: ["notes", markedAsReply],
		queryFn: async () => {
			if (!markedAsReply) return null;
			// The note being replied to is usually a colleague's, so the request names where it is
			// being read: the thread the caller already has open. The server checks the note hangs
			// there and lets the parent's read right decide.
			const response = await client.notes[":noteId"].$get({
				param: {
					noteId: markedAsReply,
				},
				query: { notesTable, identifier },
			});
			const data = await responseHandler(response);
			return data.note;
		},
	});

	/** Computed */
	if (!markedAsReply) return null;

	/** Render */
	return (
		<div className="justify-between bg-primary-50 dark:bg-primary-800 py-1 pr-1 pl-2 border border-color border-dashed rounded-md h-stack text-sm">
			<div className="h-stack">
				<Icon
					name="Reply"
					className="stroke-orange-500"
				/>
				{isFetching ? (
					<Skeleton />
				) : (
					<div className="h-stack">
						<p>Answering to </p>
						<UserCard
							userId={note?.createdBy ?? ""}
							variant="plain"
						/>
					</div>
				)}
			</div>
			<Button
				size="xxs"
				variant="ghost"
				icon={<Icon name="X" />}
				onClick={() => resetMarkedAsReply(null)}
			/>
		</div>
	);
}
